import { useState } from "react";
import { HandCoins, MessageSquare, Pencil, ShoppingBag, Check, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, uuid, vals } from "@/lib/format";
import { BigButton } from "@/components/ui-kit";
import { NumPad, AmountDisplay } from "@/components/NumPad";
import CustomerFormDialog from "@/components/customers/CustomerFormDialog";
import CustomerVehicles from "@/components/customers/CustomerVehicles";
import { usePdfReport } from "@/components/PdfReport";
import { buildStatement } from "@/lib/reports";

function RepayDialog({ customer, open, onOpenChange }) {
  const { t, money, dispatch } = useApp();
  const [amount, setAmount] = useState("");
  const save = () => {
    if (dispatch("payment.create", { id: uuid(), customer_id: customer.id, customer_name: customer.name, amount: Number(amount) })) {
      toast.success(t("repayment_saved"));
      setAmount("");
      onOpenChange(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="repay-dialog">
        <DialogTitle className="font-heading text-2xl">{t("record_repayment")}</DialogTitle>
        <p className="font-bold">{customer.name} · {t("balance_owed")}: {money(customer.balance)}</p>
        <AmountDisplay value={amount} label={t("amount")} testid="repay-amount-display" />
        <button type="button" onClick={() => setAmount(String(Math.max(customer.balance, 0)))} className="h-11 rounded-xl border-2 border-[#1F9D55] text-[#1F9D55] font-bold" data-testid="repay-full-btn">{t("pay_all")}</button>
        <NumPad value={amount} onChange={setAmount} testid="repay-numpad" />
        <BigButton variant="green" disabled={!(Number(amount) > 0)} onClick={save} className="w-full" data-testid="repay-confirm-btn"><Check className="w-5 h-5" />{t("confirm")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDetail({ customerId, onClose }) {
  const { view, t, money, lang, can, online } = useApp();
  const [repay, setRepay] = useState(false);
  const [edit, setEdit] = useState(false);
  const [sending, setSending] = useState(false);
  const { view: v2, t: t2, lang: l2, currency } = useApp();
  const pdf = usePdfReport();
  const c = customerId ? view.customers[customerId] : null;
  if (!c) return null;

  const history = [
    ...vals(view.sales).filter((s) => s.customer_id === c.id).map((s) => ({ id: s.id, at: s.created_at, kind: "sale", s })),
    ...vals(view.payments).filter((p) => p.customer_id === c.id).map((p) => ({ id: p.id, at: p.created_at, kind: "pay", p })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  const remind = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/campaigns/send", { customer_ids: [c.id], message: t("tpl_reminder"), kind: "reminder" });
      toast.success(data.mocked ? t("sent_mocked") : t("sent"));
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  return (
    <Sheet open={!!c} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl" data-testid="customer-detail-sheet">
        <SheetTitle className="font-heading text-2xl text-start">{c.name} <span className="text-base text-muted-foreground">{c.family}</span></SheetTitle>
        <div className="max-w-2xl mx-auto w-full space-y-4 mt-3">
          <div className="rounded-2xl bg-secondary p-4 flex justify-between items-end">
            <div>
              <div className="text-sm font-bold text-muted-foreground">{t("balance_owed")}</div>
              <div className={`text-4xl font-extrabold ${c.balance > 0 ? "text-[#E67E22]" : "text-[#1F9D55]"}`} data-testid="customer-detail-balance">{money(c.balance)}</div>
            </div>
            <div className="text-end text-sm">
              {c.credit_limit > 0 && <div>{t("credit_limit")}: <b>{money(c.credit_limit)}</b></div>}
              <div>{t("points")}: <b data-testid="customer-detail-points">{c.points || 0}</b></div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <BigButton variant="green" onClick={() => setRepay(true)} data-testid="customer-debt-repay-btn" className="text-base px-2"><HandCoins className="w-5 h-5" />{t("repay")}</BigButton>
            <BigButton variant="soft" disabled={!can("marketing") || !online || !c.phone || sending} onClick={remind} data-testid="customer-whatsapp-remind-btn" className="text-base px-2"><MessageSquare className="w-5 h-5" />{t("remind")}</BigButton>
            <BigButton variant="soft" onClick={() => setEdit(true)} data-testid="customer-edit-btn" className="text-base px-2"><Pencil className="w-5 h-5" />{t("edit")}</BigButton>
          </div>
          <div>
            <BigButton variant="soft" onClick={() => pdf.exportReport(buildStatement(c, { view: v2, t: t2, lang: l2, currency }))} disabled={pdf.busy} className="w-full text-base" data-testid="customer-statement-pdf-btn"><FileDown className="w-5 h-5" />{t("pdf_statement")}</BigButton>
            {pdf.node}
          </div>
          {view.shop?.type === "autoparts" && <CustomerVehicles customer={c} onClose={onClose} />}
          <h3 className="font-heading text-lg font-bold">{t("history")}</h3>
          {history.length === 0 && <p className="text-muted-foreground">{t("no_history")}</p>}
          <div className="space-y-2" data-testid="customer-history-list">
            {history.map((h) => (
              <div key={h.id} className="rounded-xl bg-white border border-border p-3 flex items-center gap-3">
                <span className={`w-10 h-10 rounded-full grid place-items-center ${h.kind === "pay" ? "bg-[#E3F6EC] text-[#1F9D55]" : "bg-[#E67E22]/10 text-[#E67E22]"}`}>
                  {h.kind === "pay" ? <HandCoins className="w-5 h-5" /> : <ShoppingBag className="w-5 h-5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{h.kind === "pay" ? t("repayment") : `${t("purchase")} · ${t(h.s.payment === "credit" ? "pay_credit" : "pay_cash")}`}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(h.at, lang)}{(h.s || h.p).pending && ` · ${t("pending")}`}</div>
                </div>
                <div className={`font-extrabold ${h.kind === "pay" ? "text-[#1F9D55]" : ""}`}>
                  {h.kind === "pay" ? `-${money(h.p.amount)}` : money(h.s.total)}
                  {h.kind === "sale" && h.s.credit > 0 && <div className="text-xs text-[#E67E22] text-end">+{money(h.s.credit)}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <RepayDialog customer={c} open={repay} onOpenChange={setRepay} />
        <CustomerFormDialog open={edit} onOpenChange={setEdit} customer={c} />
      </SheetContent>
    </Sheet>
  );
}
